import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {DataTable} from "components/dataTable/index.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function AdminDisneyTokens(props) {
  const appDataStore = useAppDataStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedToken, setSelectedToken] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editForm, setEditForm] = useState({costPrice: "", salePrice: ""});
  const [csvFile, setCsvFile] = useState(null);
  const [csvContent, setCsvContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTokenIds, setSelectedTokenIds] = useState([]);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkNewStatus, setBulkNewStatus] = useState("");

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  const handleEditToken = (token) => {
    setSelectedToken(token);
    setEditForm({
      costPrice: token.costPrice || "",
      salePrice: token.salePrice || ""
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const {name, value} = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitEditToken = async () => {
    try {
      if (!editForm.costPrice && !editForm.salePrice) {
        alert("Por favor ingresa al menos un precio");
        return;
      }

      setLoading(true);
      await appDataStore.post("/disney-tokens/update", {
        id: selectedToken.id,
        costPrice: editForm.costPrice ? parseFloat(editForm.costPrice) : undefined,
        salePrice: editForm.salePrice ? parseFloat(editForm.salePrice) : undefined
      });

      alert("Token actualizado exitosamente");
      setShowEditModal(false);
      setSelectedToken(null);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al actualizar token:", err);
      alert("Error al actualizar token: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (token, newStatus) => {
    if (!confirm(`¿Cambiar estado del token a ${newStatus}?`)) {
      return;
    }

    try {
      setLoading(true);
      await appDataStore.post("/disney-tokens/change-status", {
        id: token.id,
        status: newStatus
      });

      alert("Estado actualizado exitosamente");
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al cambiar estado:", err);
      alert("Error al cambiar estado: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvContent(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const submitCsvImport = async () => {
    try {
      if (!csvContent) {
        alert("Por favor selecciona un archivo CSV");
        return;
      }

      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/import-csv", {
        csvContent
      });

      alert(
        `Importación completada:\n` +
        `Total: ${response.total}\n` +
        `Exitosos: ${response.success}\n` +
        `Errores: ${response.errors}`
      );

      setShowUploadModal(false);
      setCsvFile(null);
      setCsvContent("");
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al importar CSV:", err);
      alert("Error al importar CSV: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectToken = (tokenId, isSelected) => {
    setSelectedTokenIds(prev => {
      if (isSelected) {
        return [...prev, tokenId];
      } else {
        return prev.filter(id => id !== tokenId);
      }
    });
  };

  const handleSelectAllTokens = (table) => {
    const allTokenIds = table.getRowModel().rows.map(row => row.original.id);
    if (selectedTokenIds.length === allTokenIds.length) {
      setSelectedTokenIds([]);
    } else {
      setSelectedTokenIds(allTokenIds);
    }
  };

  const submitBulkStatusChange = async () => {
    try {
      if (!bulkNewStatus) {
        alert("Por favor selecciona un estado");
        return;
      }

      if (!selectedTokenIds.length) {
        alert("Por favor selecciona al menos un token");
        return;
      }

      if (!confirm(`¿Cambiar estado a "${bulkNewStatus}" para ${selectedTokenIds.length} tokens?`)) {
        return;
      }

      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/bulk-status", {
        ids: selectedTokenIds,
        status: bulkNewStatus
      });

      alert(`Estados actualizados: ${response.updated} tokens`);
      setShowBulkStatusModal(false);
      setBulkNewStatus("");
      setSelectedTokenIds([]);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al cambiar estados:", err);
      alert("Error al cambiar estados: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({status}) => {
    const statusColors = {
      available: "bg-green-100 text-green-800",
      used: "bg-blue-100 text-blue-800",
      expired: "bg-red-100 text-red-800",
      error: "bg-gray-100 text-gray-800"
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || "bg-gray-100 text-gray-800"}`}>
        {status}
      </span>
    );
  };

  const columns = useMemo(() => ([
    {
      id: 'select',
      header: ({table}) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={() => handleSelectAllTokens(table)}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
        />
      ),
      cell: ({row}) => (
        <input
          type="checkbox"
          checked={selectedTokenIds.includes(row.original.id)}
          onChange={(e) => handleSelectToken(row.original.id, e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
        />
      ),
      enableColumnFilter: false,
      size: 40
    },
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({cell}) => {
        return cell.getValue();
      }
    },
    {
      accessorKey: 'subjectId',
      header: 'Subject ID',
    },
    {
      accessorKey: 'entitlementId',
      header: 'Entitlement ID',
    },
    {
      accessorKey: 'product',
      header: 'Producto',
      opFilter: 'eq',
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => <StatusBadge status={cell.getValue()}/>
    },
    {
      accessorKey: 'costPrice',
      header: 'Costo',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.costPrice
    },
    {
      accessorKey: 'salePrice',
      header: 'Precio Venta',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.salePrice
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha Creación',
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      cell: ({row}) => (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleEditToken(row.original)}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
            disabled={loading}
          >
            Editar
          </button>
          {row.original.status === 'available' && (
            <button
              onClick={() => handleChangeStatus(row.original, 'used')}
              className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs"
              disabled={loading}
            >
              Marcar Usado
            </button>
          )}
          {row.original.status !== 'expired' && (
            <button
              onClick={() => handleChangeStatus(row.original, 'expired')}
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
              disabled={loading}
            >
              Expirar
            </button>
          )}
        </div>
      )
    }
  ]), [selectedTokenIds, loading]);

  const dataFetch = useCallback(async (
    records,
    currentPage,
    order,
    search,
    filter
  ) => {
    try {
      const requestData = {
        records,
        currentPage,
        order,
        search,
        filter,
        attributes: ['id', 'subjectId', 'entitlementId', 'product', 'status', 'providerId', 'costPrice', 'salePrice', 'createdAt'],
        searchAttributes: ['subjectId', 'entitlementId', 'product', 'providerId']
      };

      const response = await appDataStore.post("/disney-tokens/list", requestData);

      return {
        data: response.data || [],
        totalRows: response.total || 0,
        totalPages: response.totalPages || 1
      };
    } catch (err) {
      console.error("Error al cargar tokens:", err);
      return {
        data: [],
        totalRows: 0,
        totalPages: 1
      };
    }
  }, [appDataStore]);

  if (!hasMasterScope) {
    return (
      <>
        <div className="p-2 max-w-7xl mx-auto text-black">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Tokens Disney</h2>
          <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
                <p className="text-red-700">
                  No tienes permisos para acceder a la gestión de tokens. Se requiere el scope <strong>Master</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-2  mx-auto text-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800"></h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-medium"
              disabled={loading}
            >
              📤 Importar CSV
            </button>
            {selectedTokenIds.length > 0 && (
              <button
                onClick={() => setShowBulkStatusModal(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium"
                disabled={loading}
              >
                ⚙️ Cambiar Estado ({selectedTokenIds.length})
              </button>
            )}
          </div>
        </div>

        <DataTable
          key={refreshKey}
          columns={columns}
          dataFetch={dataFetch}
          enablePagination
          enableSearch
          enableColumnFilter
          enableExport
          exportFileName="disney-tokens"
        />
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Editar Token</h3>

            <div className="space-y-4 mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Subject ID</p>
                <p className="text-gray-800 font-medium">{selectedToken.subjectId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Entitlement ID</p>
                <p className="text-gray-800 font-medium">{selectedToken.entitlementId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Producto</p>
                <p className="text-gray-800 font-medium">{selectedToken.product}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Costo
                </label>
                <input
                  type="number"
                  name="costPrice"
                  value={editForm.costPrice}
                  onChange={handleEditFormChange}
                  placeholder="0.00"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio de Venta
                </label>
                <input
                  type="number"
                  name="salePrice"
                  value={editForm.salePrice}
                  onChange={handleEditFormChange}
                  placeholder="0.00"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={submitEditToken}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded font-medium"
                disabled={loading}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Importar Tokens desde CSV</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Archivo CSV
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              {csvFile && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Archivo seleccionado: {csvFile.name}
                </p>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded">
              <p className="font-medium mb-2">Formato esperado:</p>
              <p>El CSV debe contener las columnas:</p>
              <ul className="list-disc list-inside mt-1 text-xs">
                <li>Subject ID</li>
                <li>Entitlement ID</li>
                <li>Product</li>
                <li>Nuplin Token</li>
              </ul>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setCsvFile(null);
                  setCsvContent("");
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={submitCsvImport}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-medium"
                disabled={loading || !csvContent}
              >
                Importar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cambiar Estado - {selectedTokenIds.length} Tokens</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Estado
              </label>
              <select
                value={bulkNewStatus}
                onChange={(e) => setBulkNewStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">Seleccionar...</option>
                <option value="available">Disponible</option>
                <option value="used">Usado</option>
                <option value="expired">Expirado</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowBulkStatusModal(false);
                  setBulkNewStatus("");
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={submitBulkStatusChange}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium"
                disabled={loading || !bulkNewStatus}
              >
                Cambiar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});